<script setup lang="ts">
import ArchetypePage from '~/components/archetypes/ArchetypePage.vue';

useSeoMeta({
  title: 'Evaluating GMP AI Before and After Release — QikSolve',
  description:
    'A practical AI governance approach to evaluating GMP AI use cases before release and monitoring quality, review, and effectiveness after release.',
});

const page = {
  hero: {
    eyebrow: 'AI evaluation and effectiveness',
    title: 'Evaluate AI before release and keep learning after release',
    summary:
      'Good quality is sustained when AI governance evaluation criteria are defined before use, effectiveness is reviewed after release, and findings lead to action.',
    cta: { label: 'Discuss your GMP AI pathway', to: '/contact' },
  },
  intro: {
    title: 'Evaluation is part of the operating model',
    content:
      'A one-time launch check is not enough when an AI-assisted workflow, its data, its prompts, or its surrounding process can change. Evaluation should connect intended use, quality expectations, review evidence, and improvement.',
    items: [
      { title: 'Define success before release', description: 'Set evaluation criteria, acceptance thresholds, review scope, and failure handling before the workflow is used.' },
      { title: 'Monitor the operating reality', description: 'Watch output quality, review outcomes, exceptions, missed reviews, and escalation signals after release.' },
      { title: 'Turn findings into improvement', description: 'Use deviations, review findings, and effectiveness checks to improve the workflow under control.' },
    ],
  },
  sections: [
    {
      title: 'Questions for pre-release evaluation',
      items: [
        { title: 'Is intended use clear?', description: 'The process, decision, data, output, reviewer, and evidence expectations should be explicit.' },
        { title: 'Are acceptance thresholds defined?', description: 'Decide what the team must demonstrate before the use case is allowed to proceed.' },
        { title: 'Are failure paths testable?', description: 'Define what happens when output is uncertain, incomplete, incorrect, or unavailable.' },
      ],
    },
    {
      title: 'Questions for post-release review',
      items: [
        { title: 'What signals matter?', description: 'Monitor quality, review, exception, escalation, and process signals rather than infrastructure health alone.' },
        { title: 'Who owns the response?', description: 'Name the decision owner when a threshold is breached or a control is missed.' },
        { title: 'When is re-evaluation needed?', description: 'Connect material changes and observed drift to a documented re-evaluation decision.' },
      ],
    },
  ],
  callout: {
    title: 'Evaluation turns trust into an observable practice',
    content: 'QikSolve helps teams plan proportionate evaluation and effectiveness review. This page does not replace a customer validation protocol or regulated quality decision.',
    tone: 'boundary' as const,
  },
  relatedLinks: {
    title: 'Continue the decision',
    items: [
      { label: 'Practical, governed AI hub', description: 'Return to the full Pillar 5 governance pathway.', to: '/product/agentic-ai-governance' },
      { label: 'Controlled change', description: 'Connect evaluation to changes in models, prompts, rules, and workflows.', to: '/product/controlled-change-for-gmp-ai-workflows' },
      { label: 'Evidence and traceability', description: 'Define the evidence needed to understand review and effectiveness.', to: '/product/evidence-and-traceability-for-gmp-ai' },
      { label: 'Scaling GMP AI through existing quality systems', description: 'Consider how governance and evaluation relate to a staged operating model.', to: '/product/scaling-gmp-ai-through-existing-quality-systems' },
    ],
  },
  cta: { title: 'Need an evaluation path for a GMP AI use case?', content: 'Bring the intended use, quality criteria, and review signals your team needs to define.', label: 'Discuss your pathway', to: '/contact' },
};
</script>

<template>
  <ArchetypePage v-bind="page" />
</template>
